import axios from "axios";
import API_URL from "../Config";

export const OBSERVATION_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
];

export const OBSERVATION_TYPE_OPTIONS = [
  { value: "unsafe_act", label: "Unsafe Act" },
  { value: "unsafe_condition", label: "Unsafe Condition" },
  { value: "near_miss", label: "Near Miss" },
];

export const OBSERVATION_SEVERITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const getAuthHeaders = () => ({
  Authorization: localStorage.getItem("authKey") || "",
});

export const parseApiError = (
  error,
  fallbackMessage = "Something went wrong",
) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.message) {
    return error.message;
  }

  return fallbackMessage;
};

export const fetchDepartmentOptions = async () => {
  const response = await axios.get(`${API_URL}/api/get_departments`, {
    headers: getAuthHeaders(),
  });

  return (Array.isArray(response.data) ? response.data : []).map(
    (department) => ({
      label: department.DepartmentName,
      value: department.ID,
    }),
  );
};

export const fetchUserOptions = async () => {
  const response = await axios.get(`${API_URL}/api/get_user`, {
    headers: getAuthHeaders(),
  });

  return (Array.isArray(response.data) ? response.data : []).map((user) => ({
    label: user.Name,
    value: user.ID,
  }));
};

export const getOptionByValue = (options, value) =>
  options.find((option) => option.value === value) || null;

export const formatDateDisplay = (dateString) => {
  if (!dateString) return "NA";

  const trimmedDateString = String(dateString).trim();
  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) return "NA";

  const hasExplicitTimezone = /(GMT|UTC|Z|[+-]\d{2}:?\d{2})$/i.test(
    trimmedDateString,
  );

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(hasExplicitTimezone ? { timeZone: "UTC" } : {}),
  }).format(parsedDate);
};

export const normalizeDateToYyyyMmDd = (dateValue) => {
  if (!dateValue) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateValue).trim())) {
    return String(dateValue).trim();
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const year = parsedDate.getUTCFullYear();
  const month = String(parsedDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const buildObservationFormData = (formData, documents = []) => {
  const payload = new FormData();

  payload.append("title", formData.title.trim());
  payload.append("type", formData.type);
  payload.append("severity", formData.severity);
  payload.append("description", formData.description.trim());
  payload.append("location", formData.location.trim());
  payload.append("observer_name", (formData.observer_name || "").trim());
  payload.append(
    "observed_date",
    normalizeDateToYyyyMmDd(formData.observed_date),
  );

  if (formData.department_id) {
    payload.append("department_id", formData.department_id);
  }

  documents.forEach((document) => {
    if (document instanceof File) {
      payload.append("attachments", document);
    }
  });

  return payload;
};

export const isExistingObservationAttachment = (document) =>
  Boolean(document?.id);

export const getObservationAttachmentName = (document) =>
  document?.filename || document?.name || "Document";

export const getObservationAttachmentSizeLabel = (document) => {
  if (document instanceof File) {
    return `${(document.size / 1024).toFixed(2)} KB`;
  }

  const numericSize = Number(document?.file_size);
  if (!Number.isNaN(numericSize) && numericSize > 0) {
    return `${numericSize.toFixed(2)} KB`;
  }

  if (typeof document?.file_size === "string" && document.file_size.trim()) {
    return document.file_size;
  }

  return "Size unavailable";
};

export const getObservationTypeLabel = (type) =>
  getOptionByValue(OBSERVATION_TYPE_OPTIONS, type)?.label || "NA";

export const getObservationSeverityLabel = (severity) =>
  getOptionByValue(OBSERVATION_SEVERITY_OPTIONS, severity)?.label || "NA";

export const getObservationStatusLabel = (status) =>
  getOptionByValue(OBSERVATION_STATUS_OPTIONS, status)?.label || "NA";

export const getStatusColor = (status) => {
  switch (status) {
    case "open":
      return "#C0612B";
    case "in_progress":
      return "#2980B9";
    case "closed":
      return "#2E8B57";
    default:
      return "#6b7280";
  }
};

const fetchSafetyFileBlob = async (fileId) =>
  axios.get(`${API_URL}/api/safety/file/${fileId}`, {
    headers: getAuthHeaders(),
    responseType: "blob",
  });

export const downloadSafetyFile = async (fileId, filename) => {
  const response = await fetchSafetyFileBlob(fileId);
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");

  link.href = blobUrl;
  link.download = filename || "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

export const openSafetyFile = async (fileId) => {
  const response = await fetchSafetyFileBlob(fileId);
  const blobUrl = window.URL.createObjectURL(response.data);
  window.open(blobUrl, "_blank", "noopener,noreferrer");

  window.setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 60000);
};
